import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu } from 'lucide-react';
import { currentPublicBrand } from '../../lib/publicBrand';

const navLinks = [
  { href: '/', label: 'Product' },
  { href: '/pricing', label: 'Compare plans' },
  { href: '/contact', label: 'Contact' },
];

const mobileSecondaryLinks = [
  { href: '/contact?type=platform_demo', label: 'Request Invite' },
];

export default function Header({ minimal = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const brand = currentPublicBrand();
  const visibleNavLinks = brand.isCareDock
    ? [{ href: '/', label: 'Platform' }, { href: '/contact?type=platform_demo', label: 'Provider enquiries' }]
    : navLinks;
  const visibleMobileLinks = brand.isCareDock
    ? [{ href: '/access', label: 'Workspace access' }]
    : mobileSecondaryLinks;

  return (
    <header className={brand.isCareDock
      ? "caredock-platform-theme sticky top-0 z-50 border-b border-[#CBE7F6] bg-white/95 backdrop-blur-xl"
      : "sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E4E8EB]"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            {brand.isCareDock ? (
              <img src={brand.wordmark} alt="CareDock" className="h-8 w-auto max-w-[148px] sm:h-9 sm:max-w-[198px]" />
            ) : (
              <>
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-xl">O</span>
                </div>
                <span className="font-heading font-semibold text-xl text-text-primary hidden sm:block">
                  Osabea CareOps
                </span>
              </>
            )}
          </Link>

          {!minimal && (
            <>
              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                {visibleNavLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      location.pathname === link.href ? 'text-primary' : 'text-text-muted'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Desktop CTAs */}
              <div className="hidden lg:flex items-center gap-3">
                <Link to="/access">
                  <Button
                    variant="outline"
                    className="rounded-full px-6 border-primary text-primary hover:bg-primary/5"
                    data-testid="header-login-btn"
                  >
                    Sign in
                  </Button>
                </Link>
                <Link to="/contact?type=platform_demo">
                  <Button 
                    className="bg-primary hover:bg-primary-hover text-white rounded-full px-6"
                    data-testid="header-book-demo-btn"
                  >
                    {brand.isCareDock ? 'Provider enquiries' : 'Request Invite'}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {minimal && (
            <Link to={brand.isCareDock
              ? (location.pathname === '/contact' ? '/access' : '/contact?type=platform_demo')
              : '/access'}>
              <Button
                variant="outline"
                className="rounded-full px-5 border-primary text-primary hover:bg-primary/5"
                data-testid={brand.isCareDock
                  ? (location.pathname === '/contact' ? 'header-access-btn' : 'header-provider-enquiries-btn')
                  : 'header-login-btn'}
              >
                {brand.isCareDock
                  ? (location.pathname === '/contact' ? 'Access portal' : 'Provider enquiries')
                  : 'Sign in'}
              </Button>
            </Link>
          )}

          {/* Mobile Menu */}
          {!minimal && (
          <div className="lg:hidden flex items-center gap-2">
            <Link to="/contact?type=platform_demo">
              <Button
                size="sm"
                className="bg-primary hover:bg-primary-hover text-white rounded-full"
                data-testid="mobile-book-demo-btn"
              >
                {brand.isCareDock ? 'Enquiries' : 'Invite'}
              </Button>
            </Link>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white">
                <div className="flex flex-col gap-6 mt-8">
                  {visibleNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium transition-colors hover:text-primary ${
                        location.pathname === link.href ? 'text-primary' : 'text-text-primary'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/access"
                    onClick={() => setIsOpen(false)}
                    className={`text-lg font-medium transition-colors hover:text-primary ${
                      location.pathname === '/access' ? 'text-primary' : 'text-text-primary'
                    }`}
                  >
                    Sign in
                  </Link>
                  <div className="border-t border-[#E4E8EB] pt-6">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-4">Access</p>
                    <div className="flex flex-col gap-4">
                      {visibleMobileLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setIsOpen(false)}
                          className={`text-base font-medium transition-colors hover:text-primary ${
                            location.pathname === link.href ? 'text-primary' : 'text-text-primary'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          )}
        </div>
      </div>
    </header>
  );
}
